import { toLocalizedNumber } from "@configs/utils/utility";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import CheckIcon from "../../../assets/icons/Check";
import InProgressIcon from "../../../assets/icons/InProgress";
import Progress from "../../../components/Common/Progress";
import type { Trigger } from "../../../configs/apis/models";
import { Operator, TriggerId } from "../../../configs/models/constants";

export type GroupCardProps = {
  triggers: Trigger[];
};

const GroupCard = ({ triggers }: GroupCardProps) => {
  const { t: translate } = useTranslation("translation");

  const getIsCompleted = (trigger: Trigger): boolean => {
    if (trigger?.progress === 100) return true;
    return false;
  };

  const getTriggerType = (trigger: Trigger) => {
    const localizedNumber = toLocalizedNumber(trigger.actionLeft);
    return translate("only_left", { 0: localizedNumber });
  };

  return (
    <div className="flex w-full flex-col">
      {triggers?.map((trigger, idx) => (
        <div key={`${trigger.triggerType}-${trigger.triggerFrequency}`}>
          <div className="flex w-full items-center gap-4">
            <div className="flex flex-col items-center">
              {idx !== 0 && (
                <div
                  className={clsx(
                    "h-3 w-[1px]",
                    getIsCompleted(trigger) ? "bg-successColor" : "bg-activeColor",
                  )}
                />
              )}
              {getIsCompleted(trigger) ? (
                <div className="flex aspect-square items-center justify-center rounded-full border border-successColor p-3 text-successColor">
                  <CheckIcon />
                </div>
              ) : (
                <div className="flex aspect-square items-center justify-center rounded-full border border-activeColor p-3 text-activeColor">
                  <InProgressIcon />
                </div>
              )}
              {idx !== triggers.length - 1 && (
                <div
                  className={clsx(
                    "h-3 w-[1px]",
                    getIsCompleted(trigger) ? "bg-successColor" : "bg-activeColor",
                  )}
                />
              )}
            </div>

            <div className="w-full pb-3">
              <p className="font-semibold">
                {getIsCompleted(trigger)
                  ? translate("completed")
                  : trigger.triggerType === TriggerId.TIER ? translate("upgrade_your_tier") : getTriggerType(trigger)}
              </p>
              <div className="flex items-center gap-1">
                <Progress
                  className={clsx(getIsCompleted(trigger) && "bg-successColor")}
                  percentage={trigger.progress}
                />
                <span className="text-sm">{trigger.progress}%</span>
              </div>
            </div>
          </div>
          {idx !== triggers.length - 1 && (
            <div className="my-1 ms-[22.5px] flex flex-col justify-center">
              {triggers.length > 1 && triggers[0].operator === Operator.AND ? (
                <span className="text-xs font-bold">{translate("and")}</span>
              ) : (
                <span className="text-xs font-bold">{translate("or")}</span>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default GroupCard;
